// The casino lobby (casino.md §5, ECONOMY · CASINO).
//
// The lobby is what a player reads who holds no seat: the club head, the
// house rules in one line, the stake tiers in ONE pill group, the four
// games, one QUICK JOIN a game, the queue card while the quick queue holds
// this player, and the open tables anybody may join.
//
// **The board is the mess board of mockup round 101 card 01** (R2165,
// casino.md §16.1): one row a game with its drawn mark in the club ink, a
// dotted leader to the price, the house line with the club's part of it,
// and a live state. Roulette leads with its next spin. The head is the club
// head of card 04 (`club.ts`).
//
// **The stake stands once** (R2143). The pill group over the cards names
// the tier, so a card states only what differs from game to game: its
// name, its live state, the share the house keeps and, at hold'em, the
// buy-in. A refusal stands on the card it is true of: the hold'em card
// alone says that the buy-in is too high.
//
// The module is pure. It takes the frames and the reader's own picks, and
// it gives back one string. `CasinoPanel` owns the host, the local tier
// pick and every act, so a test reads the lobby without a document.
//
// **Every figure comes from the hello catalog** (`CasinoRules`, R355), and
// every sentence comes from `gen/strings.ts`. Nothing here states a price,
// a payout or a timeout of its own.
//
// **The name of a game is its own key.** The locale carries
// `casino_game_roulette`, `casino_game_blackjack`, `casino_game_slots` and
// `casino_game_holdem`, so the name never comes out of a house sentence
// and no language has to put its name first (R988).

import {
  CasinoGame, type CasinoLobby, type CasinoLobbyTable, CasinoPhase, CasinoQueueKind,
  type CasinoRules, type CasinoStake,
} from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { credits } from '../../arcade/format';
import { groupThousands } from '../../locale/group';
import { armedCtl, ctl, stakeRow } from './cards';
import { casinoFillLeftSec, casinoWaitedSec, stakeAt } from './casinostate';
import { clubHeadHtml, clubRowLine } from './club';
import { part, type GameCtx } from './gameview';
import { pct } from './pct';
import { gameMark } from './symbols';

/** The four games, in the order the lobby draws them. */
const LOBBY_GAMES: readonly CasinoGame[] = [
  CasinoGame.CASINO_GAME_ROULETTE,
  CasinoGame.CASINO_GAME_BLACKJACK,
  CasinoGame.CASINO_GAME_SLOTS,
  CasinoGame.CASINO_GAME_HOLDEM,
];

/** What the lobby needs beside the game context. */
export interface LobbyCtx extends GameCtx {
  /** The last lobby frame. The panel draws no lobby without one. */
  lobby: CasinoLobby;
  /**
   * True while the reader holds a seat and the board stands beside the
   * table in the casino pane (R2198). Every press that seats the reader is
   * then off: the server seats one reader at one table.
   */
  seated?: boolean;
}

/** The separator the house sentences and the table row already use. */
const PART = '·';

/**
 * The share of every stake the wheel keeps, in percent: one minus what a
 * bet kind pays back over the whole wheel. Every kind of the table gives
 * the same figure, so the first row of the rule table states it (casino.md
 * §4.1, R2752). A table with no row states 0.
 */
function wheelEdgePct(rules: CasinoRules): number {
  const row = rules.roulette[0];
  if (!row || rules.roulettePockets <= 0) return 0;
  return 100 * (1 - ((row.pays + 1) * row.covers) / rules.roulettePockets);
}

/**
 * The whole house sentence of one game, with its figures from the rule
 * table.
 *
 * Every game states the share the house keeps, and the rule table gives
 * every one of the four figures: the payouts and the pockets of the wheel,
 * the payback of the machine, the rake of hold'em and the edge of
 * blackjack, which the server measures for the shoe, the stand rule and
 * the price of a natural (`CasinoRules.blackjack_edge_pct`). Hold'em also states the cap
 * of its share in the credits of the picked tier `stake`, else the lowest
 * tier (R2142), so the reader knows it before sitting down.
 */
export function houseLine(game: CasinoGame, rules: CasinoRules, stake: CasinoStake | null = null): string {
  switch (game) {
    case CasinoGame.CASINO_GAME_ROULETTE:
      return S.casino_house_roulette({ pct: pct(wheelEdgePct(rules)) });
    case CasinoGame.CASINO_GAME_SLOTS:
      return S.casino_house_slots({ pct: pct(100 - rules.slotsRtpPct) });
    case CasinoGame.CASINO_GAME_HOLDEM:
      return S.casino_house_holdem({
        pct: pct(rules.rakePct * 100),
        cap: credits(rules.rakeCapBb * ((stake ?? rules.stakes[0])?.minBet ?? 0)),
      });
    default:
      return S.casino_house_blackjack({
        decks: groupThousands(rules.blackjackDecks), pct: pct(rules.blackjackEdgePct),
      });
  }
}

/** The name of each game, in the reader's language, built once at load (R855). */
const GAME_NAME: Partial<Record<CasinoGame, string>> = {
  [CasinoGame.CASINO_GAME_ROULETTE]: S.casino_game_roulette,
  [CasinoGame.CASINO_GAME_BLACKJACK]: S.casino_game_blackjack,
  [CasinoGame.CASINO_GAME_SLOTS]: S.casino_game_slots,
  [CasinoGame.CASINO_GAME_HOLDEM]: S.casino_game_holdem,
};

/** The name of one game. A game the table does not name reads as blackjack. */
export function gameName(game: CasinoGame): string {
  return GAME_NAME[game] ?? S.casino_game_blackjack;
}

/**
 * What one seat at one game costs before the first card falls.
 *
 * Hold'em asks for a buy-in, and the rule table already multiplied it out
 * of big blinds. Every house game asks for the table minimum.
 */
export function entryCost(game: CasinoGame, stake: CasinoStake): number {
  return game === CasinoGame.CASINO_GAME_HOLDEM ? stake.buyInMin : stake.minBet;
}

/** True while the queue holds this reader. */
function inQueue(state: CasinoQueueKind): boolean {
  return state === CasinoQueueKind.CASINO_QUEUE_WAITING
    || state === CasinoQueueKind.CASINO_QUEUE_FILLING;
}

/**
 * True while the quick queue holds this reader for this game at this tier
 * (R2143). QUICK JOIN of that card then reads IN QUEUE and sends nothing,
 * because a second press would only put the reader where the reader
 * already stands. Another game or another tier still joins: the server
 * keeps one entry an empire and replaces it (casino.md §5).
 */
export function queuedFor(lobby: CasinoLobby, game: CasinoGame, tier: number): boolean {
  return inQueue(lobby.queueState) && lobby.queueGame === game && lobby.queueStakeTier === tier;
}

/**
 * The stake tier in force, from the reader's pick (R2143).
 *
 * A pick the reader can pay stands. A pick the reader cannot pay, and a
 * pick of a tier the table does not hold, fall back to the highest tier
 * the reader can pay. A reader who picked nothing takes the lowest tier,
 * the cheapest seat in the house (R1359), and so does a reader who can pay
 * no tier at all. A tier is payable when the credits pay its minimum bet,
 * the entry of every house game; hold'em states its own buy-in on its card.
 */
export function pickTier(rules: CasinoRules, credits: number, picked: number | null): CasinoStake | null {
  const low = rules.stakes[0] ?? null;
  if (picked === null) return low;
  const want = stakeAt(rules, picked);
  if (want && credits >= want.minBet) return want;
  let best: CasinoStake | null = null;
  for (const s of rules.stakes) {
    if (credits >= s.minBet && (best === null || s.minBet > best.minBet)) best = s;
  }
  return best ?? low;
}

/**
 * The one stake pill group of the lobby (R2118, R2143). A tier whose
 * minimum bet the reader cannot pay is shut, and the reason stands ONE
 * time, as the line under the row, for every game: a house card that the
 * reader cannot pay therefore draws no reason of its own.
 */
function tierPills(ctx: LobbyCtx, rules: CasinoRules, picked: CasinoStake): string {
  const list = rules.stakes.map((s) => ({
    id: `tier-${s.tier}`,
    label: S.casino_tier_pill({ min: credits(s.minBet) }),
    payable: ctx.credits >= s.minBet,
    data: { op: 'tier', tier: String(s.tier) },
  }));
  return `<div class="pills lobbytier">${stakeRow(list, `tier-${picked.tier}`, true)}</div>`;
}

/**
 * The roulette table the lobby leads with: the first one at the tier in
 * force, else the first one at any tier. Null while no wheel is open.
 */
function leadWheel(ctx: LobbyCtx, tier: number): CasinoLobbyTable | null {
  let any: CasinoLobbyTable | null = null;
  for (const t of ctx.lobby.tables) {
    if (t.game !== CasinoGame.CASINO_GAME_ROULETTE) continue;
    if (t.stakeTier === tier) return t;
    any ??= t;
  }
  return any;
}

/**
 * The next spin of the wheel the lobby leads with (R2165): the seconds of
 * its betting window, counted on from the lobby frame's arrival, or the
 * line that the wheel runs. It is a part of its own, because it steps once
 * a second and the rest of the lobby must not move under a finger
 * (`gameview.ts`, rule 3). No open wheel gives an empty part, so the shape
 * of the lobby stays.
 */
function spinPart(ctx: LobbyCtx, tier: number): string {
  const t = leadWheel(ctx, tier);
  let line = '';
  if (t && t.phase === CasinoPhase.CASINO_PHASE_SPINNING) {
    line = S.casino_wheel_runs;
  } else if (t && t.phase === CasinoPhase.CASINO_PHASE_BETTING && t.leftSec > 0) {
    const gone = Math.max(0, (ctx.nowMs - ctx.slice.lobbyAtMs) / 1000);
    const left = Math.ceil(t.leftSec - gone);
    if (left > 0) line = S.casino_next_spin({ sec: left });
  }
  return part('nextspin', 'class="next"', line, 'span');
}

/**
 * The live state of one game: the open tables of that game and the seats
 * they hold, or the line that none is open. The machine holds no table,
 * so its line says how a pull works. Roulette leads with its next spin.
 */
function liveLine(ctx: LobbyCtx, game: CasinoGame, tier: number): string {
  if (game === CasinoGame.CASINO_GAME_SLOTS) return S.casino_card_machine;
  let tables = 0;
  let seated = 0;
  for (const t of ctx.lobby.tables) {
    if (t.game !== game) continue;
    tables += 1;
    seated += t.seated;
  }
  const count = tables === 0 ? S.casino_card_quiet
    : S.casino_card_tables({ tables: groupThousands(tables), seated: groupThousands(seated) });
  return game === CasinoGame.CASINO_GAME_ROULETTE ? `${spinPart(ctx, tier)}<span>${count}</span>` : count;
}

/**
 * What one round of a game costs at the tier in force, the figure the
 * leader of the row runs to (R2165): the least bet of roulette, the least
 * hand of blackjack, one pull of the machine and the buy-in span of
 * hold'em. Each states the unit of its own game, so the row says what
 * differs and the tier pills keep the tier (R2143).
 */
function priceOf(game: CasinoGame, stake: CasinoStake): string {
  const min = credits(stake.minBet);
  switch (game) {
    case CasinoGame.CASINO_GAME_HOLDEM:
      return S.casino_buy_in({ min: credits(stake.buyInMin), max: credits(stake.buyInMax) });
    case CasinoGame.CASINO_GAME_SLOTS:
      return S.casino_price_pull({ min });
    case CasinoGame.CASINO_GAME_ROULETTE:
      return S.casino_price_bet({ min });
    default:
      return S.casino_price_hand({ min });
  }
}

/**
 * One row of the board: the drawn mark of the game in the club ink, the
 * name, a dotted leader to the price, the house line with the club's part
 * of it, the live state and the one button that takes the reader in.
 *
 * The slot machine holds no table and no seat — a pull settles at once — so
 * its button opens the machine and every other button joins the quick
 * queue. Both are one press, and the panel knows which act it is by the
 * button's own `data-op`.
 */
function gameCard(ctx: LobbyCtx, rules: CasinoRules, game: CasinoGame, stake: CasinoStake): string {
  const holdem = game === CasinoGame.CASINO_GAME_HOLDEM;
  const afford = ctx.credits >= entryCost(game, stake);
  const machine = game === CasinoGame.CASINO_GAME_SLOTS;
  const queued = !machine && queuedFor(ctx.lobby, game, stake.tier);
  // The machine has no queue and no seat, so its button OPENS the screen,
  // and the locale carries OPEN for that press. A queue that holds the
  // reader for this card reads IN QUEUE and takes no press (R2143).
  const label = machine ? S.casino_open : queued ? S.casino_queued : S.casino_quick_join;
  const cls = `primary quickjoin${afford ? '' : ' noafford'}${queued ? ' queued' : ''}`;
  const club = clubRowLine(ctx.lobby, rules);
  return `<div class="gamecard${game === CasinoGame.CASINO_GAME_ROULETTE ? ' lead' : ''}" data-game="${game}">`
    + gameMark(game)
    + '<div class="dline">'
    + `<span class="gamename">${gameName(game)}</span><span class="leader" aria-hidden="true"></span>`
    + `<span class="price${holdem ? ' terms' : ''}">${priceOf(game, stake)}</span></div>`
    + `<div class="houseline">${houseLine(game, rules, stake)}${club === '' ? '' : ` ${PART} ${club}`}</div>`
    + `<div class="st"><span class="live">${liveLine(ctx, game, stake.tier)}</span>`
    + ctl(machine ? 'machine' : 'quickjoin', label, afford && !queued && !ctx.seated, cls,
      ` data-game="${game}" data-tier="${stake.tier}"`)
    + '</div>'
    // The tier pill is shut when the reader cannot pay the minimum bet, so
    // a house game the reader reaches is a game the reader can pay. Only
    // the buy-in of hold'em can still be over the credits.
    + (afford || !holdem ? '' : `<div class="short">${S.casino_no_buy_in}</div>`)
    + '</div>';
}

/**
 * The queue card, while the quick queue holds this reader.
 *
 * The two countdowns are anchored on the lobby frame's own arrival, so they
 * count on between the frames and never run backwards. The seat bars come
 * from the wire: `CasinoLobby.queue_seats` states how many seats the queue
 * fills and `queue_filled` how many of them are taken, so the card counts
 * nothing and guesses nothing. A queue the server gave no seat count reads
 * the wait alone, because a bar that guesses is worse than no bar.
 */
function queueCard(ctx: LobbyCtx): string {
  const q = ctx.lobby;
  if (!inQueue(q.queueState)) return '';
  const seats = q.queueSeats;
  const filled = Math.min(q.queueFilled, seats);
  const bars = seats > 0
    ? `<div class="seats">${Array.from({ length: seats },
      (_, i) => `<i class="${i < filled ? 'on' : ''}"></i>`).join('')}</div>`
      + `<div class="muted">${S.casino_queue_filled({ filled, seats })}</div>`
    : '';
  const left = casinoFillLeftSec(ctx.slice, ctx.nowMs);
  const fill = left > 0
    ? `<div class="muted">${S.casino_queue_fill_in({ sec: Math.ceil(left) })}</div>` : '';
  return '<div class="queuecard">'
    + `<div class="state">${S.casino_queue_wait}</div>`
    + `<div class="muted">${gameName(q.queueGame)} ${PART} `
    + `${S.casino_queue_waited({ sec: Math.floor(casinoWaitedSec(ctx.slice, ctx.nowMs)) })}</div>`
    + bars
    + fill
    + armedCtl('queueleave', 0, ctx.armLabel(QUEUE_LEAVE_KEY, S.casino_queue_leave, S.casino_queue_leave_armed),
      ctx.armed(QUEUE_LEAVE_KEY), 'danger')
    + '</div>';
}

/**
 * The arm key of LEAVE QUEUE.
 *
 * `Overlay.armKey` builds the key from the button's `data-act` and its
 * `data-id`, so every armed casino button must carry a `data-id` of its
 * own. The queue holds no id, and no table carries the id 0, so the queue's
 * own control takes it.
 */
const QUEUE_LEAVE_KEY = 'casino:0';

/** One open table anybody may join. */
function tableRow(ctx: LobbyCtx, rules: CasinoRules, t: CasinoLobbyTable): string {
  const stake = stakeAt(rules, t.stakeTier);
  const full = t.seated >= t.seats;
  const mine = ctx.lobby.yourTable === t.id;
  return `<div class="row"><span class="grow">${S.casino_table_row({
    game: gameName(t.game),
    tier: stake ? S.casino_tier_pill({ min: credits(stake.minBet) }) : '',
    seated: t.seated,
    seats: t.seats,
  })}</span>`
    + `${ctl('jointable', S.casino_join, !(full || mine || ctx.seated), 'primary', ` data-id="${t.id}"`)}</div>`;
}

/** The whole lobby, as one string. */
export function lobbyHtml(ctx: LobbyCtx): string {
  const rules = ctx.rules;
  // The hello has not landed, so no figure is known yet. The lobby states
  // the one thing that is true and nothing it cannot price.
  if (!rules) return `<div class="empty">${S.casino_no_table}</div>`;
  const stake = pickTier(rules, ctx.credits, ctx.tier);
  if (!stake) return `<div class="empty">${S.casino_no_table}</div>`;
  // The club head (R2165), then the house rules in one short line (R990,
  // R2143): the help of a game stands at the head of its own table screen.
  let html = clubHeadHtml(ctx.lobby, rules, ctx.clockSec);
  html += `<div class="rules">${S.casino_lobby_rules}</div>`;
  html += `<div class="hold">${S.casino_you_hold({ amount: credits(ctx.credits) })}</div>`;
  html += tierPills(ctx, rules, stake);
  html += `<div class="games board">${LOBBY_GAMES
    .map((g) => gameCard(ctx, rules, g, stake)).join('')}</div>`;
  html += queueCard(ctx);
  html += `<h3>${S.casino_open_tables}</h3>`;
  html += ctx.lobby.tables.length === 0
    ? `<div class="empty">${S.casino_no_table}</div>`
    : ctx.lobby.tables.map((t) => tableRow(ctx, rules, t)).join('');
  return html;
}
